import { supabase } from './supabase';

// Token blacklist entry interface
interface BlacklistEntry {
  token_hash: string;
  user_id: string;
  blacklisted_at: string;
  expires_at: string;
  reason: string;
}

// Blacklist reasons
export enum BlacklistReason {
  LOGOUT = 'logout',
  SECURITY_BREACH = 'security_breach',
  PASSWORD_CHANGE = 'password_change',
  ADMIN_ACTION = 'admin_action',
  SESSION_TIMEOUT = 'session_timeout'
}

export class TokenBlacklistService {
  private static instance: TokenBlacklistService;
  private localBlacklist = new Set<string>();
  private cleanupInterval: NodeJS.Timeout | null = null;

  private constructor() {
    this.setupCleanupInterval();
  }

  public static getInstance(): TokenBlacklistService {
    if (!TokenBlacklistService.instance) {
      TokenBlacklistService.instance = new TokenBlacklistService();
    }
    return TokenBlacklistService.instance;
  }

  /**
   * Creates a hash of the token for storage
   */
  private async hashToken(token: string): Promise<string> {
    // Simple hash function for demo purposes
    // In production, use a proper cryptographic hash function
    const encoder = new TextEncoder();
    const data = encoder.encode(token);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Adds a token to the blacklist
   */
  public async blacklistToken(
    token: string, 
    userId: string, 
    reason: BlacklistReason = BlacklistReason.LOGOUT,
    expiresAt?: Date
  ): Promise<boolean> {
    try {
      const tokenHash = await this.hashToken(token);
      
      // Add to local blacklist immediately
      this.localBlacklist.add(tokenHash);
      
      // Calculate expiration time (default to 24 hours from now)
      const expiration = expiresAt || new Date(Date.now() + 24 * 60 * 60 * 1000);
      
      // Try to add to database blacklist
      const { error } = await supabase
        .from('token_blacklist')
        .insert({
          token_hash: tokenHash,
          user_id: userId,
          blacklisted_at: new Date().toISOString(),
          expires_at: expiration.toISOString(),
          reason: reason
        });

      if (error) {
        console.error('TokenBlacklistService: Error adding to database blacklist:', error);
        // Keep in local blacklist even if database fails
        return true;
      }

      console.log(`TokenBlacklistService: Token blacklisted for reason: ${reason}`);
      return true;
    } catch (error) {
      console.error('TokenBlacklistService: Error blacklisting token:', error);
      return false;
    }
  }

  /**
   * Checks if a token is blacklisted
   */
  public async isTokenBlacklisted(token: string): Promise<boolean> {
    try {
      const tokenHash = await this.hashToken(token);
      
      // Check local blacklist first (faster)
      if (this.localBlacklist.has(tokenHash)) {
        return true;
      }

      // Check database blacklist
      const { data, error } = await supabase
        .from('token_blacklist')
        .select('expires_at')
        .eq('token_hash', tokenHash)
        .single();

      if (error) {
        // If error is "not found", token is not blacklisted
        if (error.code === 'PGRST116') {
          return false;
        }
        console.error('TokenBlacklistService: Error checking blacklist:', error);
        return false;
      }

      if (data) {
        // Check if the blacklist entry has expired
        const expiresAt = new Date(data.expires_at);
        const now = new Date();
        
        if (now > expiresAt) {
          // Entry has expired, remove it
          await this.removeExpiredEntry(tokenHash);
          return false;
        }
        
        // Add to local blacklist for faster future checks
        this.localBlacklist.add(tokenHash);
        return true;
      }

      return false;
    } catch (error) {
      console.error('TokenBlacklistService: Error checking token blacklist:', error);
      return false;
    }
  }

  /**
   * Blacklists all tokens for a user
   */
  public async blacklistAllUserTokens(
    userId: string, 
    reason: BlacklistReason = BlacklistReason.SECURITY_BREACH
  ): Promise<boolean> {
    try {
      // Get current session to blacklist current token
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.access_token) {
        await this.blacklistToken(session.access_token, userId, reason);
      }

      // In a real implementation, you might want to:
      // 1. Invalidate all refresh tokens for the user
      // 2. Force re-authentication on all devices
      // 3. Log the security event
      
      console.log(`TokenBlacklistService: All tokens blacklisted for user ${userId}`);
      return true;
    } catch (error) {
      console.error('TokenBlacklistService: Error blacklisting all user tokens:', error);
      return false;
    }
  }

  /**
   * Removes an expired blacklist entry
   */
  private async removeExpiredEntry(tokenHash: string): Promise<void> {
    try {
      await supabase
        .from('token_blacklist')
        .delete()
        .eq('token_hash', tokenHash);
      
      this.localBlacklist.delete(tokenHash);
    } catch (error) {
      console.error('TokenBlacklistService: Error removing expired entry:', error);
    }
  }

  /**
   * Cleans up expired blacklist entries
   */
  public async cleanupExpiredEntries(): Promise<void> {
    try {
      const now = new Date().toISOString();
      
      const { error } = await supabase
        .from('token_blacklist')
        .delete()
        .lt('expires_at', now);

      if (error) {
        console.error('TokenBlacklistService: Error cleaning up expired entries:', error);
      } else {
        console.log('TokenBlacklistService: Cleaned up expired blacklist entries');
      }
      
      // Clear local blacklist periodically to prevent memory leaks
      this.localBlacklist.clear();
    } catch (error) {
      console.error('TokenBlacklistService: Error in cleanup:', error);
    }
  }

  /**
   * Sets up automatic cleanup of expired entries
   */
  private setupCleanupInterval(): void {
    // Clean up expired entries every hour
    this.cleanupInterval = setInterval(() => {
      this.cleanupExpiredEntries();
    }, 60 * 60 * 1000); // 1 hour
  }

  /**
   * Validates the current session token
   */
  public async validateCurrentSession(): Promise<boolean> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        return false;
      }

      const isBlacklisted = await this.isTokenBlacklisted(session.access_token);
      
      if (isBlacklisted) {
        console.log('TokenBlacklistService: Current session token is blacklisted');
        // Force logout
        await supabase.auth.signOut();
        return false;
      }

      return true;
    } catch (error) {
      console.error('TokenBlacklistService: Error validating current session:', error);
      return false;
    }
  }

  /**
   * Gets blacklist statistics for a user
   */
  public async getUserBlacklistStats(userId: string): Promise<{
    totalBlacklisted: number;
    activeBlacklisted: number;
    recentBlacklisted: number;
  }> {
    try {
      const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const now = new Date().toISOString();

      const { data: totalData } = await supabase
        .from('token_blacklist')
        .select('id', { count: 'exact' })
        .eq('user_id', userId);

      const { data: activeData } = await supabase
        .from('token_blacklist')
        .select('id', { count: 'exact' })
        .eq('user_id', userId)
        .gt('expires_at', now);

      const { data: recentData } = await supabase
        .from('token_blacklist')
        .select('id', { count: 'exact' })
        .eq('user_id', userId)
        .gte('blacklisted_at', twentyFourHoursAgo);

      return {
        totalBlacklisted: totalData?.length || 0,
        activeBlacklisted: activeData?.length || 0,
        recentBlacklisted: recentData?.length || 0
      };
    } catch (error) {
      console.error('TokenBlacklistService: Error getting blacklist stats:', error);
      return {
        totalBlacklisted: 0,
        activeBlacklisted: 0,
        recentBlacklisted: 0
      };
    }
  }

  /**
   * Destroys the service and cleans up resources
   */
  public destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    this.localBlacklist.clear();
  }
}

// Export singleton instance
export const tokenBlacklistService = TokenBlacklistService.getInstance();